import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as SaveTitleIcon }  from '../../../assets/save-outline-rounded_title.svg';
import CustomTextField from 'components/Inputs/CustomTextField';
import { saveSearchedQuery } from 'service/service';
import { formatQuery } from 'react-querybuilder';
import eventEmitter from 'SnackBarEventEmitter';

interface SaveAsModalProps {
    setIsSaveAsModalOpen: any;
	query: any;
	queryPreference: any;
	selectedCheckbox: any;
	selGroupPaths: any;
	isProjectDetails: any;
	onSavedQueriesChange?: any;
	isCost?: boolean;
	customMonth?: string;
	filterMonth?: string;
	sectorName?: string;
}

const SaveAsModal: React.FC<SaveAsModalProps> = ({ 
	setIsSaveAsModalOpen, 
	query, 
	queryPreference, 
	selectedCheckbox,
	selGroupPaths,
	isProjectDetails,
	onSavedQueriesChange,
	isCost = false,
	customMonth,
	filterMonth,
	sectorName }: any) => {
	const theme= useTheme();
	const colors = theme.palette;
	const [queryName, setQueryName] = useState<string>('');
	const [saveBtnLoader, setSaveBtnLoader] = useState(false);
	const [formattedQuery, setFormattedQuery] = useState<any>('');

	const handleOnChange = (event: any) => {
		const value = (event && event?.target?.value) ?? '';
		setQueryName(value.trim() === '' ? '' : value);
	};

	const handleSaveQuery = async (event: any) => {
		try {
			if (saveBtnLoader) return;
			setSaveBtnLoader(true);
			event.preventDefault();
			const storageKey = isProjectDetails ? 'resource-details_user_col_pref' : 'resource-list_user_col_pref';
			const fieldsStr = localStorage.getItem(storageKey);
			const fields = fieldsStr ? JSON.parse(fieldsStr) : [];
			const formPayload = {
				queryName: queryName,
				query: formattedQuery,
				queryJson: query,
				selectedColumns: fields,
				queryPreference: queryPreference,
				selectedPaths: selectedCheckbox,
				groupPaths: selGroupPaths,
				program : '',
				project : '',
				isSaveQuery: true,
				...(isCost && {
					isCost,
					dateRange: filterMonth || customMonth,
					sector: sectorName || 'all'
				})
			};
			const result = await saveSearchedQuery(formPayload);
			if(result.success) {
				setQueryName('');
				onSavedQueriesChange?.();
				setSaveBtnLoader(false);
			}
		} catch (err) {
			console.error(err);
			if (typeof err === 'object' && err !== null && 'response' in err) {
				const error = err as { response: { status: number } };
				if (error.response.status === 503)
					eventEmitter.emit('showSnackbar', 'Service temporarily unavailable. Please try again later.', 'info');
				else if (error.response.status === 504) eventEmitter.emit('showSnackbar', 'Gateway Timeout', 'info');
			}
			setSaveBtnLoader(false);
		}
	};

	const setQueryValue = () => {
		const filteredRules = query.rules.map((rule: { field: string; operator: string; value: string; id: string }) => {
			if (rule.field === "") {
				return { field: "1", operator: "=", value: "1", id: rule.id };
			}
			return rule;
		});
		const values = formatQuery({ ...query, rules: filteredRules }, 'parameterized');
		setFormattedQuery(values);
	}

	useEffect(() => {
		setQueryValue();
	}, []);

	return (
		<Stack direction="column" p={4} position='relative' sx={{ backgroundColor: `${colors.overlay.background} !important`, color: `${colors.overlay.title} !important` }}>
			<Stack minWidth='250px'
				direction="row"
				spacing={1}
				pb={2}
				marginBottom={1}
				justifyContent="space-between"
				borderBottom={`0.5px solid ${colors.overlay.border} !important`}
			>
				<Stack direction="row" alignItems="flex-start" spacing={1}
					sx={{
						position: 'absolute',
						top: 15,
						'& svg path': {
							fill: `${colors.overlay.IconColor} !important`
						}
					}}
				>
					<SaveTitleIcon className='close-icon' style={{ width: '24px', height: '24px' }}/>
					<Typography fontSize="16px" sx={{ color: `${colors.overlay.title} !important`, fontWeight: 500 }}>Save As</Typography>
				</Stack>
				<IconButton 
					onClick={() => setIsSaveAsModalOpen(false)}
					sx={{
						position: 'absolute',
						top: 8,
						right: 6,
						color: `${colors.overlay.IconColor} !important`
					}}
				>
					<CloseIcon sx={{ color: `${colors.overlay.IconColor} !important` }}/>
				</IconButton>
			</Stack>
			<Stack direction="column"
				spacing={1.5}
				pb={2}
				marginBottom={1}
				justifyContent="space-between">
				<Typography sx={{ color: `${colors.overlay.title} !important`, fontWeight: 500 }}>Save Current Query</Typography>
				<CustomTextField 
					label="Query Name *"
					id="queryName"
					name="queryName"
					variant="outlined"
					autoComplete="off"
					value={queryName}
					onChange={handleOnChange}
					fullWidth
					InputLabelProps={{
						sx: {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							padding: '0 6px',
							zIndex: 1,
							'&.Mui-focused': {
								color: `${colors.inputNew.inputPlaceholder} !important`,
							},
							'&.MuiInputLabel-shrink': {
								color: `${colors.inputNew.inputPlaceholder} !important`,
							}
						}
					}}
					InputProps={{
						sx: {
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.inputNew.inputBorder} !important`,
							},
							'&:hover .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.inputNew.inputBorderHover} !important`,
							},
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.inputNew.inputBorderHover} !important`,
							},
							'& .MuiInputBase-input': {
								color: `${colors.inputNew.inputText} !important`,
							}
						}
					}}
					sx={{
						'& .MuiInputLabel-root': {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							zIndex: 1
						},
						'& .MuiInputLabel-root.Mui-focused': {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							padding: '0 6px'
						},
						'& .MuiInputLabel-root.MuiInputLabel-shrink': {
							color: `${colors.inputNew.inputPlaceholder} !important`,
							padding: '0 6px'
						},
						'& .MuiOutlinedInput-root': {
							border: 'none'
						}
					}}
				/>
				<Stack direction="row" spacing={1.5}>
					<Button sx={{border: `1px solid ${colors.buttons.primary.borderedBorder} !important`, color: `${colors.buttons.primary.color} !important`, flex: 1}} 
						variant="pepwiseSecondaryOutline2" onClick={() => setQueryName('')}>
						CANCEL
					</Button>
					<Button disabled={!queryName} variant="pepwiseSecondary" onClick={handleSaveQuery} sx={{ flex: 1 }}>
						{saveBtnLoader ? 'SAVING...' : 'SAVE'}
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default SaveAsModal;